import { useEffect, useState } from "react";
import { api } from "@/sales/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Download,
  Eye,
  Shield,
  Scale,
  CheckCircle2,
  Hash,
  CalendarDays,
  Bookmark,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { clientToFields, stashOfferPrefill } from "@/lib/offerPrefill";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICON = {
  Aanbesteding: Shield,
  Juridisch: Scale,
};

// Resolve an API-returned URL: absolute URLs (e.g. Google Drive) are returned
// as-is; relative paths are prefixed with the backend URL.
const resolveUrl = (u) => (/^https?:\/\//i.test(u) ? u : `${BACKEND_URL}${u}`);

export default function GoldenStandard() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Global client picker: a generator document can only be produced for a
  // chosen client, so the "Genereer" buttons stay disabled until one is set.
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");

  useEffect(() => {
    api
      .get("/documents")
      .then((r) => setDocs(r.data))
      .catch(() => toast.error("Kon documenten niet laden"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Client list is the pipeline's organisations (deduped, sorted).
    api
      .get("/deals")
      .then((r) => {
        const names = Array.from(
          new Set((r.data || []).map((d) => (d.municipality || "").trim()).filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b, "nl"));
        setClients(names);
      })
      .catch(() => setClients([]));
  }, []);

  const handleDownload = (doc) => {
    // Gouden Standaard ↔ internal document generator merge (overleg §2):
    // RiskaiA-branded EDTs (offer, declaration) are produced through the
    // document generator, not served as a static download. Other documents
    // open their external (Google Drive) copy.
    if (doc.source === "generator") {
      if (!selectedClient) {
        toast.error("Kies eerst een klant");
        return;
      }
      const template = doc.generator_template;
      // Template id via URL; client name via localStorage (out of logs).
      // Opens in a new tab so the Gouden Standaard stays put.
      stashOfferPrefill({
        template,
        fields: clientToFields(template, selectedClient),
      });
      window.open(
        `/internal/documents?template=${template}`,
        "_blank",
        "noopener,noreferrer",
      );
      toast.message(`${doc.title}`, {
        description: `Genereer voor ${selectedClient} via de RiskaiΛ documentgenerator`,
      });
      return;
    }
    if (!doc.download_url) {
      toast.error("Geen download beschikbaar voor dit document");
      return;
    }
    const url = resolveUrl(doc.download_url);
    const isDrive = /drive\.google\.com/.test(url);
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`${doc.title} geopend`, {
      description: isDrive
        ? "Google Drive · officiële versie"
        : `${doc.pages} pagina's · RiskaiΛ huisstijl`,
    });
  };

  const handlePreview = (doc) => {
    if (doc.source === "generator") {
      window.location.assign(`/internal/documents?template=${doc.generator_template}`);
      return;
    }
    if (!doc.preview_url) return;
    window.open(resolveUrl(doc.preview_url), "_blank", "noopener,noreferrer");
  };

  return (
    <main className="w-full max-w-[1680px] mx-auto px-6 md:px-10 pb-16">
      <section className="fade-up pt-2">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#C0C0C0] border border-[rgba(192,192,192,0.3)] rounded-full px-3 py-1">
            <CheckCircle2 size={11} /> Gecertificeerd
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8]">
            Editie 2026
          </span>
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl font-medium text-[#F8FAFC]">
          Gouden Standaard
        </h2>
        <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl leading-relaxed">
          Directe toegang tot de officiële RiskaiΛ procurement documentatie voor Nederlandse
          overheidsaanbestedingen. Geactualiseerd conform de Aanbestedingswet 2012 en de Gids
          Proportionaliteit.
        </p>
      </section>

      {/* Global client picker — gates the generator documents. */}
      <section className="fade-up mt-8 rounded-xl border border-[rgba(192,192,192,0.18)] bg-[#142539]/60 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3 md:flex-1">
          <div className="w-10 h-10 rounded-md bg-[#0B1521] border border-[rgba(192,192,192,0.3)] flex items-center justify-center flex-shrink-0">
            <Building2 size={18} className="text-[#C0C0C0]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">Klant</p>
            <p className="text-sm text-[#E2E8F0]">
              Kies een klant om een document te genereren
            </p>
          </div>
        </div>
        <div className="md:w-80">
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger
              data-testid="select-client"
              className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
            >
              <SelectValue
                placeholder={clients.length ? "Selecteer klant…" : "Geen klanten in pipeline"}
              />
            </SelectTrigger>
            <SelectContent className="bg-[#142539] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]">
              {clients.map((c) => (
                <SelectItem key={c} value={c} className="focus:bg-[#1C3A5A]">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {loading
          ? [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-[340px] bg-[#142539]/60 rounded-xl" />
            ))
          : docs.map((doc, idx) => (
              <DocumentCard
                key={doc.slug}
                doc={doc}
                index={idx}
                gated={doc.source === "generator" && !selectedClient}
                onDownload={() => handleDownload(doc)}
                onPreview={() => handlePreview(doc)}
              />
            ))}
      </section>

      <section className="mt-12 fade-up">
        <div className="rounded-xl border border-[rgba(192,192,192,0.15)] bg-[#142539]/60 p-6 md:p-7 flex flex-col md:flex-row items-start gap-5">
          <div className="w-11 h-11 rounded-md bg-[#1C3A5A] border border-[rgba(192,192,192,0.3)] flex items-center justify-center flex-shrink-0">
            <Bookmark size={18} className="text-[#C0C0C0]" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-xl text-[#F8FAFC]">
              Werkinstructie voor sales
            </h3>
            <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed max-w-3xl">
              Voeg de <span className="text-[#E2E8F0]">Model Offerte Overheid 2026</span> toe
              als bijlage bij iedere formele offerte vanaf fase{" "}
              <span className="text-[#C0C0C0]">Voorstel / Offerte</span>. De{" "}
              <span className="text-[#E2E8F0]">Verklaring van Exclusiviteit</span> is alleen
              vereist wanneer de aanbestedende dienst kiest voor een enkelvoudig onderhandse
              procedure — vraag in dat geval ondertekening aan bij de tekenbevoegde directeur.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function DocumentCard({ doc, index, gated, onDownload, onPreview }) {
  const Icon = CATEGORY_ICON[doc.category] || FileText;
  return (
    <article
      data-testid={`document-card-${doc.slug}`}
      className="fade-up group relative rounded-xl border border-[rgba(192,192,192,0.18)] bg-gradient-to-br from-[#142539] to-[#0B1521] overflow-hidden hover:border-[#C0C0C0]/60 transition-all"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      {/* Top accent strip */}
      <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#C0C0C0] to-transparent opacity-70" />

      {/* Soft radial glow on hover */}
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
           style={{ background: "radial-gradient(circle, #C0C0C0 0%, transparent 70%)" }} />

      <div className="relative p-7">
        <div className="flex items-start justify-between mb-5">
          <div className="w-12 h-12 rounded-md bg-[#0B1521] border border-[rgba(192,192,192,0.3)] flex items-center justify-center">
            <Icon size={20} className="text-[#C0C0C0]" />
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] border border-[rgba(192,192,192,0.2)] rounded-full px-2.5 py-1">
            {doc.category}
          </span>
        </div>

        <h3 className="font-serif-display text-2xl text-[#F8FAFC] leading-tight">
          {doc.title}
        </h3>
        <p className="text-xs uppercase tracking-[0.18em] text-[#C0C0C0] mt-2 mb-4">
          {doc.subtitle}
        </p>

        <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">{doc.description}</p>

        {/* Meta grid */}
        <div className="grid grid-cols-3 gap-2 mb-5 py-4 border-y border-[rgba(192,192,192,0.12)]">
          <Meta icon={Hash} label="Code" value={doc.code} mono />
          <Meta icon={Bookmark} label="Versie" value={doc.version} />
          <Meta icon={CalendarDays} label="Bijgewerkt" value={doc.updated_at} />
        </div>

        {/* Keywords */}
        <div className="flex flex-wrap gap-1.5 mb-6 min-h-[24px]">
          {doc.keywords.map((k) => (
            <span
              key={k}
              className="text-[10px] uppercase tracking-wider text-[#94A3B8] bg-[#1C3A5A]/60 border border-[rgba(192,192,192,0.12)] rounded px-2 py-0.5"
            >
              {k}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            data-testid={`download-${doc.slug}`}
            onClick={onDownload}
            disabled={gated}
            title={gated ? "Kies eerst een klant" : undefined}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C0C0C0] hover:bg-white text-[#0B1521] text-sm font-medium rounded px-4 py-2.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#C0C0C0]"
          >
            <Download size={15} />
            {doc.source === "generator" ? "Genereer" : "Download PDF"}
          </button>
          <button
            data-testid={`preview-${doc.slug}`}
            onClick={onPreview}
            className="inline-flex items-center justify-center gap-1.5 border border-[rgba(192,192,192,0.3)] hover:border-[#C0C0C0] text-[#E2E8F0] hover:text-white text-sm rounded px-3.5 py-2.5 transition-all"
          >
            <Eye size={15} />
            Voorbeeld
          </button>
        </div>

        {/* Pages caption */}
        <p className="text-[10px] uppercase tracking-wider text-[#64748B] mt-4 text-right">
          {doc.pages} pagina{doc.pages === 1 ? "" : "'s"} · PDF · A4
        </p>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-[#64748B] mb-1">
        <Icon size={10} />
        {label}
      </div>
      <p className={`text-xs text-[#E2E8F0] truncate ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
